import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DollarSign, CheckCircle2, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const ParentDirectorBilling = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent-communication/director/payments`);
      setPayments(res.data.payments || []);
    } catch {
      toast.error("Unable to load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, []);

  const confirmZelle = async (id) => {
    setConfirming(id);
    try {
      await axios.post(`${API}/parent-communication/director/payments/${id}/confirm`);
      toast.success("Payment confirmed");
      fetchPayments();
    } catch {
      toast.error("Unable to confirm");
    } finally {
      setConfirming(null);
    }
  };

  const pending = payments.filter((p) => p.status === "pending");
  const totalPending = pending.reduce((s, p) => s + p.amount, 0);

  return (
    <Card className="overflow-hidden border-2 border-amber-100 bg-white shadow-xl" data-testid="parent-director-billing-card">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <DollarSign className="h-5 w-5" />
            Quick Pay Received
            {pending.length > 0 && (
              <Badge className="bg-amber-500 text-white text-xs">{pending.length} pending · ${totalPending.toFixed(2)}</Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchPayments} disabled={loading} data-testid="director-billing-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4 max-h-[480px] overflow-y-auto space-y-3">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : payments.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="director-billing-empty">
            <DollarSign className="mx-auto h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">No parent payments yet.</p>
          </div>
        ) : (
          payments.map((p) => (
            <div key={p.id} className={`rounded-2xl border p-4 flex items-start justify-between gap-3 ${p.status === "confirmed" ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50"}`} data-testid={`director-payment-${p.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800">{p.parentName}</p>
                  <Badge className={p.status === "confirmed" ? "bg-emerald-100 text-emerald-700 text-[10px]" : p.method === "card" ? "bg-blue-100 text-blue-700 text-[10px]" : "bg-amber-100 text-amber-700 text-[10px]"}>
                    {p.status === "confirmed" ? "Confirmed" : p.method === "card" ? "Card — Pending" : "Zelle — Awaiting"}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{p.reason} · {formatDate(p.createdAt)}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-black text-slate-800">${p.amount.toFixed(2)}</p>
                {p.status === "pending" && p.method !== "card" && (
                  <Button size="sm" variant="outline" onClick={() => confirmZelle(p.id)} disabled={confirming === p.id}
                    className="mt-1 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
                    data-testid={`director-confirm-payment-${p.id}`}>
                    {confirming === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" />Confirm Received</>}
                  </Button>
                )}
                {p.status === "confirmed" && (
                  <p className="text-xs text-emerald-600 mt-1 font-medium">✓ Received</p>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};
